class GoogleReviews extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    this.endpoint = this.dataset.endpoint;
    this.apiBase = this.endpoint.replace(/\/reviews.*$/, "");

    this.allowedStoreIds = this.parseStoreIds(this.dataset.allowedStoreIds);
    this.limit = Number(this.dataset.limit || 16);

    this.locationSelect = this.querySelector("[data-location-select]");
    this.track = this.querySelector("[data-track]");
    this.dots = this.querySelector("[data-dots]");
    this.ratingElement = this.querySelector("[data-summary-rating]");
    this.reviewButton = this.querySelector("[data-write-review]");
    this.previousButton = this.querySelector("[data-prev]");
    this.nextButton = this.querySelector("[data-next]");
    this.locationPicker = this.querySelector("[data-location-picker]");
    this.locationPickerList = this.querySelector("[data-location-picker-list]");

    if (this.allowedStoreIds.length === 1 && this.locationSelect) {
      this.locationSelect.remove();
      this.locationSelect = null;
    }

    this.locations = [];
    this.currentLocations = [];
    this.currentLocation = null;
    this.overall = null;
    this.index = 0;

    this.handleResize = this.updateCarousel.bind(this);
    this.handleDocumentClick = this.onDocumentClick.bind(this);

    this.load();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  async load() {
    try {
      const storesResponse = await fetch(`${this.apiBase}/stores`);

      if (!storesResponse.ok) {
        throw new Error(`Stores request failed (${storesResponse.status})`);
      }

      const storesData = await storesResponse.json();

      this.stores = storesData.stores || [];

      if (this.allowedStoreIds.length) {
        this.stores = this.stores.filter((store) => {
          return this.allowedStoreIds.includes(store.id);
        });
      }

      this.overall = this.allowedStoreIds.length
        ? this.calculateOverallFromStores(this.stores)
        : storesData.overall || null;

      this.renderLocationSelect();
      await this.fetchReviews("all");

      this.renderLocationPicker();
      this.bindEvents();
    } catch (error) {
      console.error(error);

      this.track.innerHTML = `
        <div class="google-reviews-error">
          Unable to load Google Reviews.
        </div>
      `;
    }
  }

  bindEvents() {
    this.track.parentElement.addEventListener("scroll", () => {
      requestAnimationFrame(() => this.updateCarousel());
    });

    this.previousButton.addEventListener("click", () => {
      this.goTo(this.index - 1);
    });

    this.nextButton.addEventListener("click", () => {
      this.goTo(this.index + 1);
    });

    this.reviewButton.addEventListener("click", (event) => {
      if (this.currentLocation?.writeReviewUrl) {
        return;
      }

      event.preventDefault();
      this.toggleLocationPicker();
    });

    if (this.locationSelect) {
      this.locationSelect.addEventListener("change", () => {
        this.handleLocationChange();
      });
    }

    window.addEventListener("resize", this.handleResize);
    document.addEventListener("click", this.handleDocumentClick);
  }

  async handleLocationChange() {
    const id = this.locationSelect.value;

    this.index = 0;
    this.closeLocationPicker();

    await this.fetchReviews(id);
  }

  renderLocationSelect() {
    if (!this.locationSelect) return;

    this.locationSelect.innerHTML = `
      <option value="all">All Stores</option>
      ${this.stores
        .map((store) => {
          return `
            <option value="${this.escapeAttribute(store.id)}">
              ${this.escape(store.name)}
            </option>
          `;
        })
        .join("")}
    `;
  }

  render() {
    const reviews = this.getReviews();

    this.renderSummary();
    this.updateReviewButton();
    this.renderCards(reviews);
    this.renderReviewSchema(reviews);
    this.renderLocalBusinessSchema();

    this.renderDots();
    this.updateCarousel();
  }

  renderLocalBusinessSchema() {
    if (!this.currentLocation) {
      this.removeSchema("local-business");
      return;
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${window.location.origin}${window.location.pathname}#business`,
      name: this.currentLocation.title,
      url: window.location.href,
      sameAs: this.currentLocation.mapsUrl
        ? [this.currentLocation.mapsUrl]
        : undefined,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(this.currentLocation.summary.rating),
        reviewCount: Number(this.currentLocation.summary.reviewCount),
        bestRating: 5,
        worstRating: 1
      }
    };

    let script = this.querySelector('[data-schema="local-business"]');

    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.schema = "local-business";
      this.appendChild(script);
    }

    script.textContent = JSON.stringify(schema);
  }

  removeSchema(name) {
    const script = this.querySelector(`[data-schema="${name}"]`);

    if (script) {
      script.remove();
    }
  }

  renderReviewSchema(reviews) {
    if (!this.currentLocation) {
      this.removeSchema("google-reviews");
      return;
    }

    const schema = reviews
      .filter((review) => review.comment && review.rating)
      .map((review) => ({
        "@context": "https://schema.org",
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.author || "Google user",
        },
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(review.rating),
          bestRating: "5",
          worstRating: "1",
        },
        reviewBody: review.comment,
        datePublished: review.createdAt || review.updatedAt || undefined,
        publisher: {
          "@type": "Organization",
          name: "Google",
        },
        itemReviewed: {
          "@id": `${window.location.origin}${window.location.pathname}#business`
        },
      }));

    let script = this.querySelector('[data-schema="google-reviews"]');

    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.schema = "google-reviews";
      this.appendChild(script);
    }

    script.textContent = JSON.stringify(schema);
  }

  renderSummary() {
    const summary = this.getCurrentSummary();

    this.ratingElement.innerHTML = `
      <span>${summary.rating}</span>

      <span class="google-reviews-stars">
        ${this.stars(Math.round(Number(summary.rating)))}
      </span>

      <span class="google-reviews-count">
        (${summary.reviewCount.toLocaleString()})
      </span>
    `;
  }

  getCurrentSummary() {
    if (this.currentLocation?.summary) {
      return {
        rating: Number(this.currentLocation.summary.rating || 0).toFixed(1),
        reviewCount: Number(this.currentLocation.summary.reviewCount || 0),
      };
    }

    if (this.overall) {
      return {
        rating: Number(this.overall.rating || 0).toFixed(1),
        reviewCount: Number(this.overall.reviewCount || 0),
      };
    }

    return {
      rating: "0.0",
      reviewCount: 0,
    };
  }

  updateReviewButton() {
    this.reviewButton.textContent = "Review us on Google";

    if (this.currentLocation?.writeReviewUrl) {
      this.reviewButton.href = this.currentLocation.writeReviewUrl;
      this.reviewButton.target = "_blank";
      this.reviewButton.rel = "noopener noreferrer";
      return;
    }

    this.reviewButton.href = "#";
    this.reviewButton.removeAttribute("target");
    this.reviewButton.removeAttribute("rel");
  }

  bindReviewToggles() {
    this.querySelectorAll("[data-review-toggle]").forEach((button) => {
      button.addEventListener("click", () => {
        const text = button.previousElementSibling;
        const expanded = button.dataset.expanded === "true";

        if (expanded) {
          text.textContent = text.dataset.short;
          button.textContent = "Read more";
          button.dataset.expanded = "false";
        } else {
          text.textContent = text.dataset.full;
          button.textContent = "Show less";
          button.dataset.expanded = "true";
        }
      });
    });
  }

  renderCards(reviews) {
    if (!reviews.length) {
      this.track.innerHTML = `
        <div class="google-reviews-empty">
          No reviews found.
        </div>
      `;
      return;
    }

    this.track.innerHTML = reviews
      .map((review) => this.renderCard(review))
      .join("");

    this.bindReviewToggles();
  }

  renderCard(review) {
    const fullText = review.comment || "";
    const limit = 145;
    const isLong = fullText.length > limit;
    const shortText = isLong ? `${fullText.slice(0, limit).trim()}...` : fullText;

    const initials = this.escape(review.author || "G").charAt(0).toUpperCase();

    const avatar = review.avatar
      ? `
        <img
          src="${this.escapeAttribute(review.avatar)}"
          alt="${this.escapeAttribute(review.author)}"
          loading="lazy"
        >
      `
      : initials;

    return `
        <article class="google-review-card">
        <div class="google-review-card__header">
            <div class="google-review-card__avatar">
            ${avatar}
            <span class="google-review-card__google-badge">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="injected-svg" data-src="https://static.elfsight.com/icons/app-all-in-one-reviews-icons-google-multicolor-stroke.svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g clip-path="url(#a-32)"><path fill="#fff" stroke="#fff" stroke-linejoin="round" stroke-width="2" d="M11.854 1.023c5.965 0 10.8 4.836 10.8 10.8v.4c0 5.965-4.835 10.8-10.8 10.8-5.964 0-10.8-4.835-10.8-10.8v-.4c0-5.964 4.836-10.8 10.8-10.8Z"></path><path fill="url(#b-34)" d="M6.382 13.906a6.04 6.04 0 0 1-.307-1.909H1.998c0 1.517.343 2.946.947 4.227l.124.254v.009a10.063 10.063 0 0 0 1.889 2.604l4.47-1.674a6.119 6.119 0 0 1-3.046-3.511Z"></path><path fill="url(#c-35)" d="M18.672 4.607c-1.734-1.618-3.987-2.609-6.684-2.609-.531 1.11-.62 2.771 0 3.981 1.471 0 2.78.51 3.824 1.491l2.86-2.863Z"></path><path fill="url(#d-36)" d="M11.988 5.979h.095L11.988 2a9.936 9.936 0 0 0-7.645 3.576c.257 1.483.97 2.435 3.12 2.586 1.084-1.324 2.709-2.182 4.525-2.182Z"></path><path fill="url(#e-37)" d="M15.357 17.06c-.89.6-2.025.963-3.369.963-.784 1.263-1.31 2.562 0 3.972 2.529 0 4.674-.783 6.295-2.139l.317-.278c1.483-1.37 2.473-3.244 2.83-5.46.099-.607.149-1.24.149-1.894l-2.265.3-1.913 1.35-.036.177a4.596 4.596 0 0 1-1.813 2.871l-.194.138Z"></path><path fill="#3086FF" d="M11.997 10.183v3.863l5.368.005a4.958 4.958 0 0 1-.014.067h4.08a11.894 11.894 0 0 0-.034-3.94h-6.9v.005h-2.5Z"></path><path fill="url(#f-38)" d="m6.322 10.324.072-.227A6.136 6.136 0 0 1 8.112 7.48c-.93-.157-3.262-1.525-3.566-2.14a10.066 10.066 0 0 0-1.477 2.174A9.885 9.885 0 0 0 2 12.21c.723.312 3.082.37 4.08 0a5.927 5.927 0 0 1 .243-1.885Z"></path><path fill="url(#g-39)" d="m8.03 2.816 1.713 3.623c-.754.322-1.43.8-1.99 1.392l-3.77-1.798A10.016 10.016 0 0 1 8.03 2.816Z"></path><path fill="url(#h-40)" d="M11.988 18.023a5.733 5.733 0 0 1-3.047-.878l-1.56-.043c-1.84.488-2.373.882-2.54 1.872a9.934 9.934 0 0 0 7.147 3.021v-3.972Z"></path><path fill="url(#i-33)" d="M11.988 21.995c.421 0 .832-.021 1.231-.064v-4.016c-.388.07-.799.108-1.231.108-.412 0-.814-.044-1.202-.128v4.029c.394.047.795.071 1.202.071Z" opacity=".5"></path></g><defs><radialGradient id="b-34" cx="0" cy="0" r="1" gradientTransform="matrix(-.39757 -10.0208 14.2942 -.60134 9.338 18.94)" gradientUnits="userSpaceOnUse"><stop offset=".142" stop-color="#1ABD4D"></stop><stop offset=".248" stop-color="#6EC30D"></stop><stop offset=".312" stop-color="#8AC502"></stop><stop offset=".366" stop-color="#A2C600"></stop><stop offset=".446" stop-color="#C8C903"></stop><stop offset=".54" stop-color="#EBCB03"></stop><stop offset=".616" stop-color="#F7CD07"></stop><stop offset=".699" stop-color="#FDCD04"></stop><stop offset=".771" stop-color="#FDCE05"></stop><stop offset=".861" stop-color="#FFCE0A"></stop></radialGradient><radialGradient id="c-35" cx="0" cy="0" r="1" gradientTransform="matrix(6.98036 -.00002 -.00001 8.69591 18.395 7.264)" gradientUnits="userSpaceOnUse"><stop offset=".408" stop-color="#FB4E5A"></stop><stop offset="1" stop-color="#FF4540"></stop></radialGradient><radialGradient id="d-36" cx="0" cy="0" r="1" gradientTransform="matrix(-9.6911 5.28673 7.28367 12.9529 14.733 .725)" gradientUnits="userSpaceOnUse"><stop offset=".231" stop-color="#FF4541"></stop><stop offset=".312" stop-color="#FF4540"></stop><stop offset=".458" stop-color="#FF4640"></stop><stop offset=".54" stop-color="#FF473F"></stop><stop offset=".699" stop-color="#FF5138"></stop><stop offset=".771" stop-color="#FF5B33"></stop><stop offset=".861" stop-color="#FF6C29"></stop><stop offset="1" stop-color="#FF8C18"></stop></radialGradient><radialGradient id="e-37" cx="0" cy="0" r="1" gradientTransform="matrix(-17.1717 -22.6188 -8.27422 6.39593 12.223 20.718)" gradientUnits="userSpaceOnUse"><stop offset=".132" stop-color="#0CBA65"></stop><stop offset=".21" stop-color="#0BB86D"></stop><stop offset=".297" stop-color="#09B479"></stop><stop offset=".396" stop-color="#08AD93"></stop><stop offset=".477" stop-color="#0AA6A9"></stop><stop offset=".568" stop-color="#0D9CC6"></stop><stop offset=".667" stop-color="#1893DD"></stop><stop offset=".769" stop-color="#258BF1"></stop><stop offset=".801" stop-color="#3086FF"></stop></radialGradient><radialGradient id="f-38" cx="0" cy="0" r="1" gradientTransform="matrix(-1.20389 10.4771 14.3476 1.68068 10.996 3.988)" gradientUnits="userSpaceOnUse"><stop offset=".366" stop-color="#FF4E3A"></stop><stop offset=".458" stop-color="#FF8A1B"></stop><stop offset=".54" stop-color="#FFA312"></stop><stop offset=".616" stop-color="#FFB60C"></stop><stop offset=".771" stop-color="#FFCD0A"></stop><stop offset=".861" stop-color="#FECF0A"></stop><stop offset=".915" stop-color="#FECF08"></stop><stop offset="1" stop-color="#FDCD01"></stop></radialGradient><radialGradient id="g-39" cx="0" cy="0" r="1" gradientTransform="matrix(-3.50897 3.94947 -10.9461 -10.0722 9.372 3.77)" gradientUnits="userSpaceOnUse"><stop offset=".316" stop-color="#FF4C3C"></stop><stop offset=".604" stop-color="#FF692C"></stop><stop offset=".727" stop-color="#FF7825"></stop><stop offset=".885" stop-color="#FF8D1B"></stop><stop offset="1" stop-color="#FF9F13"></stop></radialGradient><radialGradient id="h-40" cx="0" cy="0" r="1" gradientTransform="matrix(-9.59911 -5.20571 7.21454 -12.7543 14.685 23.19)" gradientUnits="userSpaceOnUse"><stop offset=".231" stop-color="#0FBC5F"></stop><stop offset=".312" stop-color="#0FBC5F"></stop><stop offset=".366" stop-color="#0FBC5E"></stop><stop offset=".458" stop-color="#0FBC5D"></stop><stop offset=".54" stop-color="#12BC58"></stop><stop offset=".699" stop-color="#28BF3C"></stop><stop offset=".771" stop-color="#38C02B"></stop><stop offset=".861" stop-color="#52C218"></stop><stop offset=".915" stop-color="#67C30F"></stop><stop offset="1" stop-color="#86C504"></stop></radialGradient><linearGradient id="i-33" x1="10.786" x2="13.219" y1="19.945" y2="19.945" gradientUnits="userSpaceOnUse"><stop stop-color="#0FBC5C"></stop><stop offset="1" stop-color="#0CBA65"></stop></linearGradient><clipPath id="a-32"><path fill="#fff" d="M0 0h24v24H0z"></path></clipPath></defs></svg>
            </span>
            </div>

            <div class="google-review-card__meta">
            <div class="google-review-card__name-row">
                <div class="google-review-card__name">
                ${this.escape(review.author)}
                </div>

                <span class="google-review-card__verified" aria-label="Verified Google review">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" aria-hidden="true" class="verified-badge"><path fill="#197BFF" d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path></svg>
                </span>
            </div>

            <div class="google-review-card__time">
                ${this.relativeTime(review.updatedAt || review.createdAt)}
            </div>
            </div>
        </div>

        <div class="google-review-card__stars">
            ${this.stars(review.rating)}
        </div>

        <p
            class="google-review-card__text"
            data-review-text
            data-short="${this.escapeAttribute(shortText)}"
            data-full="${this.escapeAttribute(fullText)}"
        >
            ${this.escape(shortText)}
        </p>

        ${
          isLong
            ? `<button type="button" class="google-review-card__toggle" data-review-toggle>Read more</button>`
            : ""
        }

        <div class="google-review-card__location">
            <svg width="20" height="20" class="icon icon--location   " viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 13.4299C13.7231 13.4299 15.12 12.0331 15.12 10.3099C15.12 8.58681 13.7231 7.18994 12 7.18994C10.2769 7.18994 8.88 8.58681 8.88 10.3099C8.88 12.0331 10.2769 13.4299 12 13.4299Z" stroke="#292D32" stroke-width="1.5"></path>
      <path d="M3.62001 8.49C5.59001 -0.169998 18.42 -0.159997 20.38 8.5C21.53 13.58 18.37 17.88 15.6 20.54C13.59 22.48 10.41 22.48 8.39001 20.54C5.63001 17.88 2.47001 13.57 3.62001 8.49Z" stroke="#292D32" stroke-width="1.5"></path>
    </svg> ${this.escape(review.locationName)}
        </div>
        </article>
    `;
  }

  async fetchReviews(storeId = "all") {
    if (storeId === "all" && this.allowedStoreIds.length) {
      const responses = await Promise.all(
        this.allowedStoreIds.map((id) => {
          return fetch(`${this.apiBase}/reviews?store=${encodeURIComponent(id)}`);
        })
      );

      const payloads = await Promise.all(
        responses.map((response) => {
          if (!response.ok) {
            throw new Error(`Reviews request failed (${response.status})`);
          }

          return response.json();
        })
      );

      const locations = payloads
        .filter((data) => data.location)
        .map((data) => ({
          ...data.location,
          reviews: data.reviews || [],
        }));

      this.locations = locations;
      this.currentLocations = locations;
      this.currentLocation = locations.length === 1 ? locations[0] : null;
      this.overall = this.calculateOverallFromLocations(locations);

      this.render();
      return;
    }

    const response = await fetch(
      `${this.apiBase}/reviews?store=${encodeURIComponent(storeId)}`
    );

    if (!response.ok) {
      throw new Error(`Reviews request failed (${response.status})`);
    }

    const data = await response.json();

    this.overall = data.overall || this.overall;

    if (data.locations) {
      let locations = data.locations || [];

      if (this.allowedStoreIds.length) {
        locations = locations.filter((location) => {
          return this.allowedStoreIds.includes(location.id);
        });

        this.overall = this.calculateOverallFromLocations(locations);
      }

      this.locations = locations;
      this.currentLocations = locations;
      this.currentLocation = locations.length === 1 ? locations[0] : null;
    } else if (data.location) {
      const location = {
        ...data.location,
        reviews: data.reviews || [],
      };

      this.locations = [location];
      this.currentLocations = [location];
      this.currentLocation = location;
    }

    this.render();
  }

  getReviews() {
    return this.currentLocations
      .flatMap((location) => {
        return (
          location.reviews || []
        ).map((review) => ({
          ...review,
          locationName: location.name,
        }));
      })
      .filter((review) => {
        return review.comment && Number(review.rating) === 5;
      })
      .sort((a, b) => {
        return (
          new Date(
            b.updatedAt ||
            b.createdAt
          ) -
          new Date(
            a.updatedAt ||
            a.createdAt
          )
        );
      })
      .slice(0, this.limit);
  }

  renderLocationPicker() {
    if (!this.locationPickerList) return;

    const locations = (this.stores || []).filter(
      (location) => location.writeReviewUrl
    );

    this.locationPickerList.innerHTML =
      locations
        .map((location) => {
          return `
            <button
              type="button"
              class="google-reviews-picker__button"
              data-location-id="${this.escapeAttribute(
                location.id
              )}"
            >
              <span>
                ${this.escape(
                  location.name
                )}
              </span>

              <span class="google-reviews-picker__arrow">
                ↗
              </span>
            </button>
          `;
        })
        .join("");

    this.locationPickerList
      .querySelectorAll(
        "[data-location-id]"
      )
      .forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            const location = this.stores.find(
              (item) => {
                return (
                  item.id ===
                  button.dataset
                    .locationId
                );
              }
            );

            if (
              !location?.writeReviewUrl
            ) {
              return;
            }

            this.closeLocationPicker();

            window.open(
              location.writeReviewUrl,
              "_blank",
              "noopener,noreferrer"
            );
          }
        );
      });
  }

  parseStoreIds(value) {
    return String(value || "")
      .split(/\n|,|\r/)
      .map((id) => id.trim())
      .filter(Boolean);
  }

  calculateOverallFromStores(stores) {
    const reviewCount = stores.reduce((sum, store) => {
      return sum + Number(store.reviewCount || 0);
    }, 0);

    const weightedRating = stores.reduce((sum, store) => {
      return sum + Number(store.rating || 0) * Number(store.reviewCount || 0);
    }, 0);

    return {
      rating: reviewCount ? Number((weightedRating / reviewCount).toFixed(1)) : 0,
      reviewCount,
    };
  }

  calculateOverallFromLocations(locations) {
    const reviewCount = locations.reduce((sum, location) => {
      return sum + Number(location.summary?.reviewCount || 0);
    }, 0);

    const weightedRating = locations.reduce((sum, location) => {
      return sum + Number(location.summary?.rating || 0) * Number(location.summary?.reviewCount || 0);
    }, 0);

    return {
      rating: reviewCount ? Number((weightedRating / reviewCount).toFixed(1)) : 0,
      reviewCount,
    };
  }

  toggleLocationPicker() {
    if (!this.locationPicker) return;

    this.locationPicker.hidden = !this.locationPicker.hidden;
  }

  closeLocationPicker() {
    if (!this.locationPicker) return;

    this.locationPicker.hidden = true;
  }

  onDocumentClick(event) {
    if (!this.locationPicker || this.locationPicker.hidden) return;

    if (
      this.locationPicker.contains(event.target) ||
      this.reviewButton.contains(event.target)
    ) {
      return;
    }

    this.closeLocationPicker();
  }

  renderDots() {
    const totalPages = this.totalPages();

    this.dots.innerHTML = "";

    for (let index = 0; index < totalPages; index += 1) {
      const button = document.createElement("button");

      button.type = "button";
      button.className = `google-reviews-dot${index === this.index ? " is-active" : ""}`;
      button.setAttribute("aria-label", `Go to review page ${index + 1}`);

      button.addEventListener("click", () => {
        this.goTo(index);
      });

      this.dots.appendChild(button);
    }
  }

  goTo(index) {
    this.index = Math.max(0, Math.min(index, this.totalPages() - 1));

    const card = this.track.firstElementChild;
    if (!card) return;

    const gap = 20;
    const offset = this.index * this.perView() * (card.getBoundingClientRect().width + gap);

    this.track.parentElement.scrollTo({
      left: offset,
      behavior: "smooth",
    });

    this.updateCarousel();
  }

  updateCarousel() {
    const carousel = this.track.parentElement;
    const card = this.track.firstElementChild;

    if (!carousel || !card) return;

    const gap = 20;
    const pageWidth = this.perView() * (card.getBoundingClientRect().width + gap);

    this.index = Math.round(carousel.scrollLeft / pageWidth);

    this.dots.querySelectorAll("button").forEach((button, index) => {
      button.classList.toggle("is-active", index === this.index);
    });

    if (this.previousButton) {
      this.previousButton.disabled = this.index === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.index >= this.totalPages() - 1;
    }
  }

  totalPages() {
    return Math.max(1, Math.ceil(this.track.children.length / this.perView()));
  }

  perView() {
    if (window.innerWidth < 750) return 1;
    if (window.innerWidth < 990) return 2;
    return 4;
  }

  stars(rating) {
    const value = Math.max(0, Math.min(5, Number(rating) || 0));
    return "★★★★★".slice(0, value);
  }

  truncate(text, length) {
    if (!text) return "";
    if (text.length <= length) return text;

    return `${text.slice(0, length).trim()}...`;
  }

  relativeTime(date) {
    if (!date) return "";

    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);

    const intervals = [
      { label: "year", seconds: 31536000 },
      { label: "month", seconds: 2592000 },
      { label: "day", seconds: 86400 },
      { label: "hour", seconds: 3600 },
      { label: "minute", seconds: 60 }
    ];

    for (const interval of intervals) {
      const value = Math.floor(seconds / interval.seconds);

      if (value >= 1) {
        return `${value} ${interval.label}${value > 1 ? "s" : ""} ago`;
      }
    }

    return "Just now";
  }

  escape(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  escapeAttribute(value) {
    return this.escape(value).replace(/`/g, "&#96;");
  }
}

if (!customElements.get("google-reviews")) {
  customElements.define("google-reviews", GoogleReviews);
}
